import React from "react";
import { Stack, Select, NumberInput, Text, Group } from "@mantine/core";
import { NumberFieldOptions } from "./fieldOptionsTypes";

export interface NumberFieldOptionsEditorProps {
  value: NumberFieldOptions;
  onChange: (options: NumberFieldOptions) => void;
  minValue: number | null;
  maxValue: number | null;
  onMinValueChange: (value: number | null) => void;
  onMaxValueChange: (value: number | null) => void;
  maxValueError?: string;
}

const NUMBER_TYPES = [
  { value: "decimal", label: "Decimal (allows fractions)" },
  { value: "integer", label: "Integer (whole numbers only)" },
];

export const NumberFieldOptionsEditor: React.FC<
  NumberFieldOptionsEditorProps
> = ({
  value,
  onChange,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  maxValueError,
}) => {
  const handleChange = (field: keyof NumberFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const isDecimal = value.numberType !== "integer";

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure number validation options.
      </Text>

      <Select
        label="Number Type"
        description="Choose whether to allow decimal values"
        data={NUMBER_TYPES}
        value={value.numberType ?? "decimal"}
        onChange={(type) =>
          handleChange(
            "numberType",
            (type as "integer" | "decimal") ?? "decimal"
          )
        }
        allowDeselect={false}
        size="sm"
      />

      {isDecimal && (
        <NumberInput
          label="Decimal Places"
          placeholder="Unlimited"
          description="Maximum digits after decimal point"
          value={value.decimalPlaces ?? ""}
          onChange={(val) =>
            handleChange("decimalPlaces", val === "" ? undefined : val)
          }
          min={0}
          max={10}
          size="sm"
        />
      )}

      <Group grow>
        <NumberInput
          label="Min Value"
          placeholder="No min"
          value={minValue ?? ""}
          onChange={(val) => onMinValueChange(val === "" ? null : Number(val))}
          min={0}
          size="sm"
        />
        <NumberInput
          label="Max Value"
          placeholder="No max"
          value={maxValue ?? ""}
          onChange={(val) => onMaxValueChange(val === "" ? null : Number(val))}
          error={maxValueError}
          min={minValue ?? 0}
          size="sm"
        />
      </Group>
    </Stack>
  );
};
